/**
 * Gastos fijos on «Gastos y pagos»' «Por pagar» (the owner, 2026-10-07): «Toca pagar», the months
 * that fell due or fall due within the week, each recorded with one tap; the list of fixed expenses;
 * and the sheet that makes or changes one. What is due, and when, is `lib/gastosFijos.ts`'s.
 */
import { useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { DEFAULT_SETTINGS, formatMoney, hoy, newId, parseMoney, type Centavos, type CuentaBanco, type GastoFijo } from '@abastece/shared';
import { CATEGORIAS_GASTO, type CategoriaGasto } from '@abastece/core';
import { readSettings } from '../db/reader.js';
import { cuentaQueQueda } from '../lib/bancos.js';
import { CADA_MESES, leerPendientes, primerPeriodo, type Ocurrencia } from '../lib/gastosFijos.js';
import { FORMAS_GASTO, type FormaGasto } from '../lib/gastos.js';
import { dolaresDeLosLibros } from '../lib/libros.js';
import { localeActual, useT, type Clave } from '../i18n/index.js';
import { etiquetaCategoriaGasto } from '../i18n/dominio.js';
import { Campo, Interruptor, Selector } from './Campo.js';
import { ElegirEscribiendo } from './ElegirEscribiendo.js';
import { ElegirCuenta } from './ElegirCuenta.js';
import { fechaCorta } from './CuentasDeCaja.js';
import { Ico } from './Iconos.js';
import { Modal } from './Modal.js';
import { Confirmar } from './Preguntar.js';

type T = ReturnType<typeof useT>;

/** How often, as the form offers it. */
export const CADA: Record<(typeof CADA_MESES)[number], Clave> = {
  1: 'fijos.cada1',
  2: 'fijos.cada2',
  3: 'fijos.cada3',
  6: 'fijos.cada6',
  12: 'fijos.cada12',
};

/** A month with its year, as the expense it becomes is named: «octubre de 2026». */
export const nombreMes = (periodo: string): string =>
  new Date(`${periodo}-15T12:00:00Z`).toLocaleDateString(localeActual(), { month: 'long', year: 'numeric', timeZone: 'UTC' });

const soloMes = (periodo: string): string =>
  new Date(`${periodo}-15T12:00:00Z`).toLocaleDateString(localeActual(), { month: 'long', timeZone: 'UTC' });

/** «Cada mes, el día 5», «Cada 2 meses, el día 20», «Cada año, el 5 de marzo». */
export function textoFrecuencia(f: Pick<GastoFijo, 'dia' | 'cadaMeses' | 'desde'>, t: T): string {
  if (f.cadaMeses === 1) return t('fijos.cadaMes', { dia: f.dia });
  if (f.cadaMeses === 12) return t('fijos.cadaAnio', { dia: f.dia, mes: soloMes(f.desde) });
  return t('fijos.cadaMeses', { n: f.cadaMeses, dia: f.dia });
}

/**
 * Its amount as the shop writes it, or null when it changes every time. In the page's dollars
 * (`dinero`, «Gastos» read in dollars since 2026-10-13) one in córdobas is turned, and one in dollars
 * says its own, as the rest of the page writes dollars.
 */
export function montoDeFijo(f: Pick<GastoFijo, 'monto' | 'moneda'>, dinero?: (c: Centavos) => string): string | null {
  if (f.monto === null) return null;
  if (dinero) return f.moneda === 'USD' ? dolaresDeLosLibros(f.monto) : dinero(f.monto as Centavos);
  return f.moneda === 'USD' ? `US$${(f.monto / 100).toFixed(2)}` : formatMoney(f.monto as Centavos);
}

/** A due day as the rows say it: «1 sept», with its year when it is not this year's. */
function fechaDeVence(vence: string): string {
  if (vence.slice(0, 4) === hoy().slice(0, 4)) return fechaCorta(vence);
  return new Date(`${vence}T12:00:00Z`).toLocaleDateString(localeActual(), { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });
}

/**
 * The months to record: gone by in red, today's in amber, the week's in grey. The name has the
 * line to itself but for the amount: at 320 px, beside the amount and two buttons, it was a word.
 * The due day names the month, which the expense's own words then say in full.
 */
export function TocaPagar({
  pendientes,
  puedeRegistrar,
  onRegistrar,
  onOmitir,
  dinero,
}: {
  pendientes: readonly Ocurrencia[];
  puedeRegistrar: boolean;
  onRegistrar: (o: Ocurrencia) => void;
  onOmitir: (o: Ocurrencia) => void;
  /** The page's dollars, when it is read in them (`montoDeFijo`). */
  dinero?: (c: Centavos) => string;
}) {
  const t = useT();
  return (
    <section className="tarjeta" data-gastos-toca-pagar>
      <h2 className="text-sm font-semibold text-amber-200">{t('fijos.tocaPagar')}</h2>
      <p className="mb-3 text-xs text-slate-500">{t('fijos.tocaPagarAyuda')}</p>
      <ul className="flex flex-col gap-3">
        {pendientes.map((o) => {
          const monto = montoDeFijo(o.fijo, dinero);
          return (
            <li key={o.gastoId} data-ocurrencia={o.gastoId} data-estado={o.estado} className="text-sm">
              <div className="flex items-baseline justify-between gap-3">
                <p className="min-w-0 truncate">{o.fijo.descripcion}</p>
                <span className={`tabular shrink-0 ${monto === null ? 'text-slate-500' : ''}`}>{monto ?? t('fijos.varia')}</span>
              </div>
              <div className="flex items-center justify-between gap-3">
                <p
                  className={`min-w-0 text-xs ${
                    o.estado === 'vencido' ? 'text-rose-300' : o.estado === 'hoy' ? 'text-amber-300' : 'text-slate-500'
                  }`}
                  data-ocurrencia-vence=""
                >
                  {o.estado === 'vencido'
                    ? t('fijos.vencio', { fecha: fechaDeVence(o.vence) })
                    : o.estado === 'hoy'
                      ? t('fijos.venceHoy')
                      : t('fijos.vence', { fecha: fechaDeVence(o.vence) })}
                </p>
                {puedeRegistrar && (
                  <button
                    type="button"
                    className="btn-texto shrink-0 gap-1 text-xs text-marca-300"
                    data-ocurrencia-registrar=""
                    onClick={() => onRegistrar(o)}
                  >
                    <Ico icono="pagar" className="size-3.5" />
                    {t('fijos.registrar')}
                  </button>
                )}
              </div>
              {/* Under the line, as «Corregir» is under a debt from before. */}
              {puedeRegistrar && (
                <button
                  type="button"
                  className="btn-texto gap-1 text-xs text-slate-500"
                  data-ocurrencia-omitir=""
                  onClick={() => onOmitir(o)}
                >
                  <Ico icono="saltar" className="size-3.5" />
                  {t('fijos.esteMesNo')}
                </button>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

/** Every fixed expense, paused ones dimmed: a tap opens it in its sheet. */
export function ListaGastosFijos({
  fijos,
  puedeRegistrar,
  onAbrir,
  onNuevo,
  dinero,
}: {
  fijos: readonly GastoFijo[];
  puedeRegistrar: boolean;
  onAbrir: (f: GastoFijo) => void;
  onNuevo: () => void;
  /** The page's dollars, when it is read in them (`montoDeFijo`). */
  dinero?: (c: Centavos) => string;
}) {
  const t = useT();
  return (
    <section className="tarjeta" data-gastos-fijos>
      <h2 className="text-sm font-semibold text-slate-400">{t('fijos.titulo')}</h2>
      <p className="mb-2 text-xs text-slate-500">{t('fijos.ayuda')}</p>
      {fijos.length === 0 ? (
        <p className="text-xs text-slate-500">{t('fijos.ninguno')}</p>
      ) : (
        <ul className="-mx-2 flex flex-col">
          {fijos.map((f) => (
            <li key={f.id}>
              <button
                type="button"
                data-sin-icono="fila"
                data-gasto-fijo={f.id}
                disabled={!puedeRegistrar}
                onClick={() => onAbrir(f)}
                className={`flex w-full items-center justify-between gap-3 rounded-lg px-2 py-2 text-left text-sm enabled:hover:bg-slate-800/60 ${
                  f.activo ? '' : 'opacity-50'
                }`}
              >
                <span className="min-w-0">
                  <span className="block truncate">{f.descripcion}</span>
                  <span className="block text-xs text-slate-500">
                    {f.activo ? '' : `${t('fijos.enPausa')} · `}
                    {textoFrecuencia(f, t)}
                  </span>
                </span>
                <span className={`tabular shrink-0 ${f.monto === null ? 'text-slate-500' : ''}`}>{montoDeFijo(f, dinero) ?? t('fijos.varia')}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {puedeRegistrar && (
        <button type="button" className="btn-texto mt-3 gap-1 text-xs text-slate-400" data-gasto-fijo-nuevo="" onClick={onNuevo}>
          <Ico icono="nuevo" className="size-3.5" />
          {t('fijos.nuevo')}
        </button>
      )}
    </section>
  );
}

/**
 * A fixed expense in its sheet: a new one, or one to change, pause or take off.
 *
 * A new one starts counting at the first month whose day has not gone by yet; so does one whose
 * frequency changes, once the months already due are recorded or passed over. A new day only moves
 * each month's day: the months it counts are the ones it counted. Both used to start it afresh, and
 * two rents still owed were gone, or a yearly March expense became a November one (2026-10-13). Its
 * amount, words or supplier change from its next month on, and the months already recorded are
 * left as they were.
 */
export function EditarGastoFijo({
  fijo,
  proveedores,
  onGuardar,
  onQuitar,
  onCerrar,
}: {
  fijo: GastoFijo | null;
  proveedores: readonly { id: string; nombre: string; activo: boolean }[];
  onGuardar: (f: GastoFijo) => Promise<void>;
  onQuitar: (f: GastoFijo) => Promise<void>;
  onCerrar: () => void;
}) {
  const t = useT();
  const [descripcion, setDescripcion] = useState(fijo?.descripcion ?? '');
  const [categoria, setCategoria] = useState<CategoriaGasto>(fijo?.categoria ?? 'alquiler');
  const [monto, setMonto] = useState(fijo?.monto ? (fijo.monto / 100).toFixed(2) : '');
  const [moneda, setMoneda] = useState<'NIO' | 'USD'>(fijo?.moneda ?? 'NIO');
  const [cadaMeses, setCadaMeses] = useState<number>(fijo?.cadaMeses ?? 1);
  const [dia, setDia] = useState(String(fijo?.dia ?? Number(hoy().slice(8, 10))));
  const [formaPago, setFormaPago] = useState<FormaGasto>(fijo?.formaPago ?? 'efectivo_cordobas');
  const config = useLiveQuery(() => readSettings(), [], DEFAULT_SETTINGS);
  /** By transfer, the bank account it is paid from: one from before there were others, «Bancos». */
  const [cuentaElegida, setCuentaElegida] = useState<CuentaBanco | null>(
    fijo?.formaPago === 'transferencia' ? (fijo.cuenta ?? 'BANCOS') : null,
  );
  const cuenta = formaPago === 'transferencia' ? cuentaQueQueda(config, cuentaElegida, moneda) : null;
  const [proveedorId, setProveedorId] = useState(fijo?.proveedorId ?? '');
  const [activo, setActivo] = useState(fijo?.activo ?? true);
  const [quitando, setQuitando] = useState(false);
  const [trabajando, setTrabajando] = useState(false);

  const diaN = Number(dia);
  const diaValido = /^\d{1,2}$/.test(dia.trim()) && diaN >= 1 && diaN <= 31;
  const montoN = monto.trim() === '' ? null : parseMoney(monto);
  const montoValido = monto.trim() === '' || (montoN !== null && montoN > 0);
  /** Every how many months, changed: it starts counting afresh, from its next time. */
  const cambiaFrecuencia = fijo !== null && fijo.cadaMeses !== cadaMeses;
  /** Its months gone by unrecorded: a new frequency would leave them behind, so they go first. */
  const vencidos = useLiveQuery(
    async () => (fijo ? (await leerPendientes(hoy(), 0)).filter((o) => o.fijo.id === fijo.id && o.estado === 'vencido').map((o) => o.periodo) : []),
    [fijo?.id],
    [],
  );
  const dejaMeses = cambiaFrecuencia && vencidos.length > 0;
  const listo = descripcion.trim() !== '' && diaValido && montoValido && !dejaMeses;

  async function guardar() {
    if (!listo || trabajando) return;
    const cuentaDeNuevo = !fijo || cambiaFrecuencia;
    setTrabajando(true);
    try {
      await onGuardar({
        id: fijo?.id ?? newId(),
        descripcion: descripcion.trim(),
        categoria,
        moneda,
        monto: montoN,
        formaPago,
        ...(cuenta ? { cuenta } : {}),
        proveedorId: proveedorId || null,
        dia: diaN,
        cadaMeses,
        desde: cuentaDeNuevo ? primerPeriodo(diaN, hoy()) : fijo.desde,
        omitidos: fijo?.omitidos ?? [],
        activo,
      });
    } finally {
      setTrabajando(false);
    }
  }

  return (
    <Modal
      titulo={t(fijo ? 'fijos.editarTitulo' : 'fijos.nuevo')}
      onCerrar={onCerrar}
      pie={
        <button type="button" className="btn-primario w-full" disabled={!listo || trabajando} onClick={() => void guardar()}>
          <Ico icono="guardar" />
          {t('fijos.guardar')}
        </button>
      }
    >
      <div className="flex flex-col gap-4" data-gasto-fijo-hoja>
        <Campo
          etiqueta={t('gastos.deQueFue')}
          value={descripcion}
          onChange={(e) => setDescripcion(e.target.value)}
          autoFocus={!fijo}
          autoComplete="off"
          maxLength={160}
        />
        <Selector etiqueta={t('gastos.categoria')} value={categoria} onChange={(e) => setCategoria(e.target.value as CategoriaGasto)}>
          {CATEGORIAS_GASTO.map((c) => (
            <option key={c} value={c}>
              {etiquetaCategoriaGasto(c)}
            </option>
          ))}
        </Selector>
        <Campo
          etiqueta={t('fijos.monto')}
          inputMode="decimal"
          className="tabular"
          value={monto}
          onChange={(e) => setMonto(e.target.value)}
          placeholder={t('fijos.varia')}
          ayuda={t('fijos.montoAyuda')}
          error={montoValido ? undefined : t('fijos.montoInvalido')}
        />
        <Selector etiqueta={t('gastos.moneda')} value={moneda} onChange={(e) => setMoneda(e.target.value as 'NIO' | 'USD')}>
          <option value="NIO">{t('gastos.cordobas')}</option>
          <option value="USD">{t('gastos.dolares')}</option>
        </Selector>
        <Selector etiqueta={t('fijos.cada')} value={String(cadaMeses)} onChange={(e) => setCadaMeses(Number(e.target.value))}>
          {CADA_MESES.map((n) => (
            <option key={n} value={n}>
              {t(CADA[n])}
            </option>
          ))}
        </Selector>
        {dejaMeses && (
          <p role="alert" className="rounded-lg bg-amber-500/10 px-3 py-2 text-sm text-amber-200" data-gasto-fijo-vencidos>
            {t('fijos.antesLoVencido', { meses: vencidos.map(nombreMes).join(', ') })}
          </p>
        )}
        <Campo
          etiqueta={t('fijos.dia')}
          inputMode="numeric"
          className="tabular"
          value={dia}
          onChange={(e) => setDia(e.target.value)}
          ayuda={fijo ? t('fijos.cambiaDesde') : t('fijos.diaAyuda')}
          error={diaValido ? undefined : t('fijos.diaAyuda')}
        />
        <Selector etiqueta={t('fijos.formaPago')} value={formaPago} onChange={(e) => setFormaPago(e.target.value as FormaGasto)}>
          {FORMAS_GASTO.map((f) => (
            <option key={f.valor} value={f.valor}>
              {t(f.nombre)}
            </option>
          ))}
        </Selector>
        {cuenta && <ElegirCuenta moneda={moneda} valor={cuenta} onCambiar={setCuentaElegida} etiqueta={t('bancos.pagaDe')} />}
        <ElegirEscribiendo
          etiqueta={t('gastos.proveedorOpcional')}
          placeholder={t('gastos.nadieEnParticular')}
          opciones={proveedores.filter((p) => p.activo || p.id === proveedorId)}
          value={proveedorId}
          onChange={setProveedorId}
          ninguno={t('gastos.nadieEnParticular')}
          datos="proveedor"
        />
        {fijo && <Interruptor etiqueta={t('fijos.activo')} descripcion={t('fijos.activoAyuda')} checked={activo} onChange={setActivo} />}
        {fijo && (
          <button type="button" className="btn-texto gap-1.5 self-start text-sm text-rose-300" data-gasto-fijo-quitar="" onClick={() => setQuitando(true)}>
            <Ico icono="quitar" className="size-4" />
            {t('fijos.quitar')}
          </button>
        )}
      </div>
      {quitando && fijo && (
        <Confirmar
          titulo={t('fijos.quitarTitulo', { descripcion: fijo.descripcion })}
          mensaje={t('fijos.quitarMensaje')}
          confirmar={t('fijos.quitarConfirmar')}
          icono="quitar"
          peligro
          onCerrar={() => setQuitando(false)}
          onConfirmar={() => void onQuitar(fijo)}
        />
      )}
    </Modal>
  );
}
